import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../../../api/config";
import { REPORTS_FIRST_SERVICE } from "../../../../utils/constants";
import { ExtendedSessionType } from "./SendMeetingButton";

type UseCheckAutoMeetingSentParams = {
    session: ExtendedSessionType;
};

const POLLING_DELAY_MS = 2500;
const POLLING_ATTEMPTS = 3;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const fetchSessionMeetingData = async (sessionId: number) => {
    const response = await api.get(`/items/sessions/${sessionId}`, { params: { fields: "meeting_id,meeting_url" } });
    const meetingId = response.data?.data?.meeting_id as string | undefined;
    const meetingUrl = response.data?.data?.meeting_url as string | undefined;
    return { meetingId, meetingUrl };
};

export const useCheckAutoMeetingSent = ({ session }: UseCheckAutoMeetingSentParams) => {
    const queryClient = useQueryClient();
    const isReportSession = session?.service?.id === REPORTS_FIRST_SERVICE;
    const [isCheckingAutoMeeting, setIsCheckingAutoMeeting] = useState(isReportSession);

    useEffect(() => {
        if (!isReportSession) return;

        let cancelled = false;

        const invalidateSessionQueries = async () => {
            const sessionPrefix = `sessions-${session.id}`;
            await queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return typeof key === "string" && key.startsWith(sessionPrefix);
                },
            });
        };

        const pollMeetingUrl = async () => {
            for (let attempt = 0; attempt < POLLING_ATTEMPTS; attempt++) {
                if (attempt > 0) await sleep(POLLING_DELAY_MS);
                if (cancelled) return;

                try {
                    const { meetingId, meetingUrl } = await fetchSessionMeetingData(session.id);
                    if (cancelled) return;

                    if (meetingId && meetingUrl) {
                        await invalidateSessionQueries();
                        setIsCheckingAutoMeeting(false);
                        return;
                    }
                } catch {
                    if (cancelled) return;
                    continue;
                }
            }

            await invalidateSessionQueries();
            setIsCheckingAutoMeeting(false);
        };

        pollMeetingUrl();

        return () => { cancelled = true; };
    }, []);

    return { isCheckingAutoMeeting };
};
